/* Pocket Factory · P2-BACK-1, the card's back as one design: the standard tap + QR back (the default, untouched) or the
   FREE personalized back. This file is the one place both are drawn from, a pure function of the back's record:
   - the record: null (standard) or { mode: 'personalized', fill, accent, logo, art } (normalize());
   - the face of the 3D card (paintFace(): the personalized back drawn in the card, assets/pocket-card.js calls it);
   - the print file and the proof (printSvg(): CR80 plus 10% of each side, 300 dpi, the background edge to edge, no
     drawn outline but the containers' accents; renderPng() rasterizes it in a browser).
   The 3D face is the trim of the very drawing the print box holds, so what the shopper sees is what prints (rule 42).

   The standard back's artwork is recoloured, never redrawn (rules 43-44): the back file (assets/pf-back-v2-*.svg) goes
   through the same recolor P1-GRAD-1 uses (PocketCards.recolorBack: white containers, black icon, words and QR, one
   accent outline), and nothing is drawn over it but a logo in a container of the same kind. The tap symbol and the QR
   code are on every back (rule 32). Personalized: the back's colour (the brand's own, or the card's Light or Dark),
   the outline accent (the brand's own, or silver), their logo above or below the tap symbol, and an optional background
   (an image of theirs, made card-shaped by the existing AI art route when it isn't, or an AI design), filling the card
   and the print box edge to edge.

   Nothing here asks the Worker anything, reads the page, or decides a price: the record's controls are
   snippets/pf-back-personalize.liquid and assets/pf-back-personalize.js; the order line is assets/pocket.js's. Data
   only goes in an SVG's attributes as checked colours, numbers and https addresses (escaped). Docs: docs/p2/BACK-DESIGN.md. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.PFBackDesign = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';
  const HEX = /^#[a-f\d]{6}$/i;
  const hexOf = (v) => (HEX.test(v || '') ? String(v).toLowerCase() : null);
  const ASSET = /^asset_[a-f0-9]{32}$/;
  const MODES = ['standard', 'personalized'];
  const LOGO = ['off', 'top', 'bottom'];
  const SOURCES = ['ai-generated', 'customer-upload'];
  // The card's own Light and Dark (pocket-card.js cardColor()): the only colours a back takes besides the brand's.
  const LIGHT = '#fafaf7', DARK = '#20211e';
  // CR80 at 300 dpi, the back file's own frame (Bryton's template, 1011 x 638), and the print box: CR80 plus 10% of each
  // side on every side (rule 47). The Worker's art and print bleed is 3.175 mm (1086 x 713, src/cards/print.js).
  const CARD_MM = { w: 85.6, h: 53.98 }, DPI = 300, BLEED = 0.1, ART_BLEED_MM = 3.175;
  const TRIM = { Horizontal: { w: 1011, h: 638 }, Vertical: { w: 638, h: 1011 } };
  // The containers' outline, as the recolor draws it (pocket-card.js ACCENT_WIDTH, at the file's 1011 px width).
  const ACCENT_WIDTH = 13.48;
  // "fill" art's mirrored copies meet this many print pixels inside the art's edge (printSvg).
  const MIRROR_INSET = 2;
  // Where a logo goes on the back, in the back file's own frame: in a white container the width of the "Tap phone" pill,
  // above the tap symbol or under the pill (horizontal) / under the QR box (vertical), inside the 3 mm safe area and
  // clear of the containers, with the recolored file's shadow gone. Pills, as the "Tap phone" one.
  // Layout 2 (Bryton, Nov 2: "when we add the logo onto the top or onto the bottom. It doesn't shift the position of
  // everything to make that entire column aligned in the middle"): on a horizontal card the left column (their logo's
  // pill, the tap symbol and its "Tap phone" pill) is centred as one on the QR panel (centre y 318.94). The tap group moves
  // by tapDy and the logo's pill sits 28 px from it, as far as the tap symbol sits from its own pill (both outlines'
  // outer edges ~21 px apart). The tap group's drawn extent is 152.21 (ring) to 493 ("Tap phone" pill and its
  // outline); the Worker draws the same (back-design.js LAYOUT2). A vertical card keeps LOGO_BOX.
  const LAYOUT2 = {
    Horizontal: {
      top: { tapDy: 55.34, pill: { x: 112.5, y: 89.54, w: 278, h: 90 } },
      bottom: { tapDy: -62.66, pill: { x: 112.5, y: 458.34, w: 278, h: 90 } },
    },
  };
  const LOGO_BOX = {
    Horizontal: { top: { x: 112.5, y: 38, w: 278, h: 90 }, bottom: { x: 112.5, y: 506, w: 278, h: 90 } },
    Vertical: { top: { x: 180, y: 37, w: 278, h: 46 }, bottom: { x: 180, y: 926, w: 278, h: 48 } },
  };
  // A background image is card-shaped (it fills the back as it is) when its long side is within 4% of the card's own
  // shape (85.6 x 53.98) or the Worker's art shape (with its bleed), and its short side prints sharp.
  const SHAPES = [CARD_MM.w / CARD_MM.h, (CARD_MM.w + 2 * ART_BLEED_MM) / (CARD_MM.h + 2 * ART_BLEED_MM)], SHARP_SHORT = 560;

  const orientationOf = (o) => (o === 'Vertical' ? 'Vertical' : 'Horizontal');
  /** The logo pill's box on the card (the back file's frame): layout 2's on a horizontal card, else LOGO_BOX's. */
  const pillBox = (back, o) => (back.layout === 2 && LAYOUT2[o]?.[back.logo] ? LAYOUT2[o][back.logo].pill : LOGO_BOX[o][back.logo]);
  /** How far layout 2 moves the tap group for this back (0: it stays where the back file has it). */
  const tapShift = (back, o) => (back && back.logo !== 'off' && back.layout === 2 && LAYOUT2[o]?.[back.logo] ? LAYOUT2[o][back.logo].tapDy : 0);
  /** An https address, as it is; anything else is none. */
  function safeUrl(value) {
    try { const url = new URL(String(value || '')); return url.protocol === 'https:' ? url.href : null; } catch (_) { return null; }
  }
  const attr = (v) => String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const num = (v) => String(Math.round(v * 1000) / 1000);
  const channels = (hex) => hex.slice(1).match(/../g).map((x) => parseInt(x, 16));
  const gap = (a, b) => { const [x, y] = [channels(a), channels(b)]; return Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]); };

  function artOf(art) {
    const url = safeUrl(art?.url);
    if (!url) return null;
    const out = { url, fit: art.fit === 'fit' ? 'fit' : 'fill', source: SOURCES.includes(art.source) ? art.source : 'customer-upload' };
    if (ASSET.test(art.assetId || '')) out.assetId = art.assetId;
    if (art.outpainted === true) out.outpainted = true;
    return out;
  }

  /**
   * The back's record: null for the standard back (every card's default), or the personalized back, its values checked.
   * A personalized back needs its colour; everything else has a default (the automatic accent, no logo, no background).
   * @param {*} input
   * @returns {null | {mode: 'personalized', fill: string, accent: string|null, logo: 'off'|'top'|'bottom', art: object|null}}
   */
  function normalize(input) {
    if (!input || typeof input !== 'object' || input.mode !== 'personalized') return null;
    const fill = hexOf(input.fill);
    if (!fill) return null;
    const out = { mode: 'personalized', fill, accent: hexOf(input.accent), logo: LOGO.includes(input.logo) ? input.logo : 'off', art: artOf(input.art) };
    // Layout 2 (LAYOUT2) and the pill's own logo file (a light-background copy of their logo, a stored asset): only when
    // the record says so, so every record from before is the same as it was.
    if (input.layout === 2) out.layout = 2;
    if (ASSET.test(input.logoAsset || '')) out.logoAsset = input.logoAsset;
    return out;
  }
  /** "standard" or "personalized": the back's mode, as the order line says it (_Back mode). */
  const modeOf = (record) => (normalize(record) ? 'personalized' : 'standard');

  /**
   * The colours a personalized back takes: the brand's own (website colour, second colour), then the card's Light and
   * Dark; never an invented one. Near-equal colours are offered once.
   * @param {{primaryColor?: string, secondaryColor?: string}|null} brand
   * @returns {{key: 'brand'|'second'|'light'|'dark', hex: string}[]}
   */
  function fills(brand) {
    const out = [];
    const add = (key, value) => { const hex = hexOf(value); if (hex && !out.some((c) => gap(c.hex, hex) < 24)) out.push({ key, hex }); };
    add('brand', brand?.primaryColor);
    add('second', brand?.secondaryColor);
    add('light', LIGHT);
    add('dark', DARK);
    return out;
  }

  /** A personalized back to start from: their brand colour (else the card's own), the automatic accent, their logo on top. */
  function starting(brand, cardColor, { logo = true } = {}) {
    const fill = hexOf(brand?.primaryColor) || hexOf(cardColor) || LIGHT;
    return { mode: 'personalized', fill, accent: null, logo: logo ? 'top' : 'off', art: null };
  }

  /** Whether an image fills the back as it is (card-shaped and sharp enough), or is extended first (not card-shaped). */
  function cardShaped(width, height) {
    const w = Number(width), h = Number(height);
    if (!(w > 0 && h > 0)) return false;
    const ratio = Math.max(w, h) / Math.min(w, h);
    return SHAPES.some((shape) => Math.abs(ratio - shape) / shape <= 0.04) && Math.min(w, h) >= SHARP_SHORT;
  }

  /**
   * The back's geometry, in the print box's frame (300 dpi px, the back file's own scale): the trim (CR80) inside the print
   * box (10% of each side on every side), the art's box (the trim and the Worker's 3.175 mm art bleed, which "fill" art
   * covers), and the logo's box (LOGO_BOX is on the card itself, so it is moved by the bleed).
   */
  function layout(record, orientation) {
    const o = orientationOf(orientation), t = TRIM[o], back = normalize(record);
    const bleedX = t.w * BLEED, bleedY = t.h * BLEED, artBleed = (ART_BLEED_MM / CARD_MM.w) * TRIM.Horizontal.w;
    return {
      orientation: o,
      trim: { x: bleedX, y: bleedY, w: t.w, h: t.h },
      print: { w: t.w + 2 * bleedX, h: t.h + 2 * bleedY, px: { w: Math.round(t.w * (1 + 2 * BLEED)), h: Math.round(t.h * (1 + 2 * BLEED)) }, dpi: DPI },
      artBox: { x: bleedX - artBleed, y: bleedY - artBleed, w: t.w + 2 * artBleed, h: t.h + 2 * artBleed },
      // In the print box's frame, as every box here: the logo's place on the card, moved by the bleed.
      logo: back && back.logo !== 'off' ? (({ x, y, w, h }) => ({ x: x + bleedX, y: y + bleedY, w, h }))(pillBox(back, o)) : null,
      // How far the tap group moves (layout 2 with a logo, on a horizontal card), in the back file's own pixels.
      tapDy: tapShift(back, o),
    };
  }

  /** The back file's own markup, recoloured, as an inner <svg> placed over the trim (its ids made the instance's own). */
  function placedBack(svgText, recolor, accent, box, idSuffix, tapDy = 0) {
    let text = String(svgText || '');
    if (accent && typeof recolor === 'function') text = recolor(text, accent) || text;
    // Layout 2: the tap group (the back file's <g id="pfb2-tap" transform="translate(x y)">) moves with the logo's pill.
    if (tapDy) text = text.replace(/(<g id="pfb2-tap" transform="translate\()([-\d.]+) ([-\d.]+)(\))/, (m, a, x, y, b) => a + x + ' ' + num(Number(y) + tapDy) + b);
    const open = text.match(/<svg\b[^>]*>/i), close = text.lastIndexOf('</svg>');
    if (!open || close < 0) return '';
    const viewBox = (open[0].match(/viewBox="([^"]+)"/) || [])[1] || `0 0 ${box.w} ${box.h}`;
    let inner = text.slice(open.index + open[0].length, close).replace(/<!--[\s\S]*?-->/g, '');
    if (idSuffix) inner = inner.replace(/\bid="([\w-]+)"/g, `id="$1-${idSuffix}"`).replace(/url\(#([\w-]+)\)/g, `url(#$1-${idSuffix})`).replace(/href="#([\w-]+)"/g, `href="#$1-${idSuffix}"`);
    return `<svg x="${num(box.x)}" y="${num(box.y)}" width="${num(box.w)}" height="${num(box.h)}" viewBox="${attr(viewBox)}" overflow="visible">${inner}</svg>`;
  }

  /**
   * The back as drawn: an SVG of the print box (box 'print': CR80 plus 10% of each side) or of the trim alone (box 'trim':
   * the 3D card's face and the proof's card), the same drawing either way.
   * - The background fills the whole box: the back's colour edge to edge, then its art. "fill" art covers the trim and
   *   the Worker's 3.175 mm art bleed, centred (as the front's art prints), and runs on to the print box's edge mirrored,
   *   so there is no seam and nothing of the art on the card is cut; "fit" art shows all of it on the trim, on the colour.
   * - The standard back file over the trim, recoloured (white containers, black content, the accent outlines).
   * - Their logo, in a white container of the pill's kind with the same accent outline.
   * No other line is drawn: no card outline, no guide. record null: the standard back (opts.fill: the card's colour; the
   * file recoloured only with opts.accent, as a brand-styled card's is).
   * @param {object|null} record
   * @param {{orientation?: string, box?: 'print'|'trim', backSvg: string, recolor?: Function, accent?: string,
   *          fill?: string, logoHref?: string, logoPending?: boolean, artHref?: string, idSuffix?: string}} opts
   * @returns {string} SVG markup
   */
  function printSvg(record, opts = {}) {
    const back = normalize(record), g = layout(back, opts.orientation), print = opts.box !== 'trim';
    const fill = back ? back.fill : hexOf(opts.fill) || LIGHT;
    const accent = hexOf(opts.accent) || (back ? back.accent : null);
    const W = print ? g.print.w : g.trim.w, H = print ? g.print.h : g.trim.h;
    const dx = print ? 0 : -g.trim.x, dy = print ? 0 : -g.trim.y;
    const at = (box) => ({ x: box.x + dx, y: box.y + dy, w: box.w, h: box.h });
    const trim = at(g.trim), parts = [];
    const pxW = print ? g.print.px.w : g.trim.w, pxH = print ? g.print.px.h : g.trim.h;
    // The box's own size in mm, drawn on whole pixels: stretched the fraction of a pixel that takes, never letterboxed (an
    // edge left half drawn would be a seam).
    parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${pxW}" height="${pxH}" viewBox="0 0 ${num(W)} ${num(H)}" preserveAspectRatio="none">`);
    parts.push(`<rect width="${num(W)}" height="${num(H)}" fill="${fill}"/>`);
    const href = back?.art ? hrefOf(opts.artHref) : null;
    if (href) {
      if (back.art.fit === 'fit') {
        parts.push(`<image href="${attr(href)}" x="${num(trim.x)}" y="${num(trim.y)}" width="${num(trim.w)}" height="${num(trim.h)}" preserveAspectRatio="xMidYMid meet"/>`);
      } else {
        const a = at(g.artBox), id = 'pfbd-art' + (opts.idSuffix ? '-' + opts.idSuffix : '');
        // The art's mirror across each side and corner, and the art itself: the band past the art's own bleed continues it
        // seamlessly. Each copy meets its neighbour on a line MIRROR_INSET inside the art's edge, where both show the same
        // point of the art: the image's own outermost pixels (an edge its scaling may draw lighter or darker) never show,
        // and the copy drawn first always reaches past that line under the one drawn over it, so the colour behind never
        // shows between them. The corners first, whole; then each side between the two lines across it; the art last,
        // inside all four. The lines fall on whole pixels of the print file, so no pixel is part one copy and part another
        // (an art with see-through layers, drawn part-covered layer by layer, would show a light line there).
        const k = MIRROR_INSET, sx = pxW / W, sy = pxH / H, onPixel = (v, s) => Math.round(v * s) / s;
        const L = onPixel(a.x + k, sx), R = onPixel(a.x + a.w - k, sx), T = onPixel(a.y + k, sy), B = onPixel(a.y + a.h - k, sy), far = 2 * (W + H);
        const clip = (name, x, y, w, h) => `<clipPath id="${id}-${name}"><rect x="${num(x)}" y="${num(y)}" width="${num(w)}" height="${num(h)}"/></clipPath>`;
        parts.push(`<defs><image id="${id}" href="${attr(href)}" x="${num(a.x)}" y="${num(a.y)}" width="${num(a.w)}" height="${num(a.h)}" preserveAspectRatio="xMidYMid slice"/>`
          + clip('c', L, T, R - L, B - T) + clip('h', -far, T, 2 * far, B - T) + clip('v', L, -far, R - L, 2 * far) + '</defs>');
        const copy = (i, j, within) => {
          const use = `<use href="#${id}"` + (i || j ? ` transform="translate(${num(i < 0 ? 2 * L : i > 0 ? 2 * R : 0)} ${num(j < 0 ? 2 * T : j > 0 ? 2 * B : 0)}) scale(${i ? -1 : 1} ${j ? -1 : 1})"` : '') + '/>';
          return within ? `<g clip-path="url(#${id}-${within})">${use}</g>` : use;
        };
        for (const i of [-1, 1]) for (const j of [-1, 1]) parts.push(copy(i, j));
        parts.push(copy(-1, 0, 'h'), copy(1, 0, 'h'), copy(0, -1, 'v'), copy(0, 1, 'v'), copy(0, 0, 'c'));
      }
    }
    parts.push(placedBack(opts.backSvg, opts.recolor, accent, trim, opts.idSuffix, g.tapDy));
    const logo = g.logo && hrefOf(opts.logoHref);
    // logoPending: the pill's own file is still being chosen (assets/pocket-card.js pillLogo): the pill, empty, keeps its place.
    if (logo || (g.logo && opts.logoPending)) {
      const box = at(g.logo), pad = box.h * 0.12, sideways = box.h * 0.34;
      parts.push(`<rect x="${num(box.x)}" y="${num(box.y)}" width="${num(box.w)}" height="${num(box.h)}" rx="${num(box.h / 2)}" fill="#ffffff"${accent ? ` stroke="${accent}" stroke-width="${ACCENT_WIDTH}"` : ''}/>`);
      if (logo) parts.push(`<image href="${attr(logo)}" x="${num(box.x + sideways)}" y="${num(box.y + pad)}" width="${num(box.w - 2 * sideways)}" height="${num(box.h - 2 * pad)}" preserveAspectRatio="xMidYMid meet"/>`);
    }
    parts.push('</svg>');
    return parts.join('');
  }
  /** A data: image (an embedded file for the print render), as it is; anything else is none. */
  function dataImage(value) { return /^data:image\/(png|jpeg|webp|gif|svg\+xml);base64,[a-z0-9+/=]+$/i.test(String(value || '')) ? String(value) : null; }
  /** An image's address as a drawing may use it: https, an embedded data: image, or this page's own blob: (a logo the
      page made, as its background removed). */
  function hrefOf(value) { return safeUrl(value) || dataImage(value) || (/^blob:https?:\/\/[^\s"<>]+$/.test(String(value || '')) ? String(value) : null); }

  /* ---------- In a browser ---------- */
  // The standard back files' text, fetched once each (their recolor needs it); null until it is here.
  const backTexts = new Map();
  /** The back file's text, or null while it loads (ready() is called once it is here). */
  function backText(win, src, ready) {
    if (!src) return null;
    const entry = backTexts.get(src);
    if (entry?.text) return entry.text;
    if (entry) { if (ready) entry.waiting.add(ready); return null; }
    const made = { text: null, waiting: new Set(ready ? [ready] : []) };
    backTexts.set(src, made);
    if (!win?.fetch) return null;
    win.fetch(src).then((r) => (r.ok ? r.text() : Promise.reject(r.status))).then((text) => {
      if (!/<svg\b/i.test(text)) throw new Error('not an svg');
      made.text = text;
      for (const done of made.waiting) { try { done(text); } catch (_) {} }
      made.waiting.clear();
    }).catch(() => { backTexts.delete(src); });
    return null;
  }

  let faces = 0;
  /**
   * Draws the personalized back on a back card (.pc-card[data-side=back]): the trim of printSvg() in the card, over its
   * own colour, the standard art image set aside while it shows. ctx: { orientation, accent, logoHref, logoPending, backSvg, recolor }.
   * Returns false (and draws nothing) without the back file's text; clearFace() gives the card its standard back again.
   */
  function paintFace(card, record, ctx = {}) {
    const back = normalize(record);
    if (!card || !back || !ctx.backSvg) { clearFace(card); return false; }
    const doc = card.ownerDocument;
    let face = card.querySelector(':scope > [data-pf-back-face]');
    if (!face) {
      face = doc.createElement('span');
      face.dataset.pfBackFace = String(++faces);
      face.className = 'pc-back-face';
      face.setAttribute('aria-hidden', 'true');
      // Inline, like the front's art layer: every card surface draws it the same, the cart drawer's too.
      face.style.cssText = 'position:absolute;inset:0;display:block;border-radius:inherit;overflow:hidden;pointer-events:none';
      card.prepend(face);
    }
    const key = JSON.stringify([back, ctx.orientation, ctx.accent, ctx.logoHref || '', !!ctx.logoPending, (ctx.backSvg || '').length]);
    if (face.dataset.key !== key) {
      face.dataset.key = key;
      face.innerHTML = printSvg(back, { ...ctx, box: 'trim', idSuffix: 'f' + face.dataset.pfBackFace });
      const svg = face.firstElementChild;
      if (svg) { svg.setAttribute('width', '100%'); svg.setAttribute('height', '100%'); svg.setAttribute('preserveAspectRatio', 'none'); svg.style.display = 'block'; }
    }
    card.dataset.pfBack = 'personalized';
    for (const art of card.querySelectorAll(':scope > [data-card-art]')) art.hidden = true;
    card.style.setProperty('--card-color', back.fill);
    // The face is the whole card, printed edge to edge: no border ring of the card's own colour around a background.
    card.style.borderWidth = '0px';
    return true;
  }
  /** The card's standard back again (its own art image shown, the personalized face gone). */
  function clearFace(card) {
    if (!card || card.dataset.pfBack !== 'personalized') return;
    card.querySelector(':scope > [data-pf-back-face]')?.remove();
    delete card.dataset.pfBack;
    card.style.removeProperty('border-width');
    for (const art of card.querySelectorAll(':scope > [data-card-art]')) art.hidden = false;
  }

  /** A file at an address, as a data: URL (for the print render, whose images must be in it). */
  async function dataUrlOf(win, url) {
    if (!url) return '';
    if (dataImage(url)) return url;
    const response = await win.fetch(url, { credentials: 'omit' });
    if (!response.ok) throw new Error('image ' + response.status);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => { const reader = new win.FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(reader.error); reader.readAsDataURL(blob); });
  }
  /**
   * The print file (or the proof's card, box 'trim') as a PNG Blob, drawn in this browser from printSvg(): its images
   * fetched and put in the drawing first (an SVG drawn as an image loads nothing of its own).
   * opts: as printSvg's, with backSrc (the back file's address) and logoUrl / artUrl in place of their hrefs.
   */
  async function renderPng(win, record, opts = {}) {
    const backSvg = opts.backSvg || await (await win.fetch(opts.backSrc)).text();
    const [logoHref, artHref] = await Promise.all([opts.logoUrl ? dataUrlOf(win, opts.logoUrl) : '', normalize(record)?.art ? dataUrlOf(win, opts.artUrl || normalize(record).art.url) : '']);
    const svg = printSvg(record, { ...opts, backSvg, logoHref, artHref });
    const size = svg.match(/^<svg[^>]*width="(\d+)" height="(\d+)"/);
    const width = Number(size?.[1]) || 1213, height = Number(size?.[2]) || 766;
    const url = win.URL.createObjectURL(new win.Blob([svg], { type: 'image/svg+xml' }));
    try {
      const image = new win.Image();
      image.decoding = 'sync';
      await new Promise((resolve, reject) => { image.onload = resolve; image.onerror = () => reject(new Error('the back did not draw')); image.src = url; });
      const canvas = win.document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const g = canvas.getContext('2d');
      g.drawImage(image, 0, 0, width, height);
      return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    } finally { win.URL.revokeObjectURL(url); }
  }

  return { MODES, LOGO, LIGHT, DARK, CARD_MM, DPI, BLEED, TRIM, LOGO_BOX, LAYOUT2, ACCENT_WIDTH, MIRROR_INSET, normalize, modeOf, fills, starting, cardShaped, layout, printSvg, backText, paintFace, clearFace, renderPng, safeUrl };
});
